import React, { useState } from 'react';
import axios from '../../../../api/myAxios';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormHelperText,
  Alert,
  Fade,
  CircularProgress,
} from '@mui/material';
import { TextFields as TextFieldsIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const DataTextUpload = ({
  workspaceId,
  onSuccess,
  isEditMode = false,
  editingItem = null,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [text, setText] = useState(editingItem?.content || '');
  const [description, setDescription] = useState(
    editingItem?.description || ''
  );
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // 🚀 REACT QUERY MUTATION POUR TEXT UPLOAD/UPDATE
  const uploadTextMutation = useMutation({
    mutationFn: async ({ text, workspaceId, description, isEdit, itemId }) => {
      if (isEdit) {
        const response = await axios.put(`/api/data/text/${itemId}`, {
          text,
          description,
        });
        return response.data;
      } else {
        const response = await axios.post('/api/data/text', {
          text,
          description,
          workspaceId,
        });
        return response.data;
      }
    },
    onSuccess: (data) => {
      // 🔥 INVALIDATION AUTOMATIQUE DES QUERIES
      queryClient.invalidateQueries({
        queryKey: ['dataItems', workspaceId],
      });

      // Reset form
      setText('');
      setHasAttemptedSubmit(false);

      // Callback externe si fourni
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      console.error(t('error.console.errorUploadingText'), error);
    },
  });

  const handleTextChange = (e) => {
    setText(e.target.value);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);

    if (!text.trim()) {
      return; // L'erreur sera gérée par la validation
    }

    uploadTextMutation.mutate({
      text,
      workspaceId,
      description,
      isEdit: isEditMode,
      itemId: editingItem?._id,
    });
  };

  // 🔥 VALIDATION DES ERREURS
  const getTextError = () => {
    if (!hasAttemptedSubmit) return null;
    if (!text.trim()) return t('dataTextUpload.errorNoText');
    return null;
  };

  const textError = getTextError();
  const isFormValid =
    text.trim() && !textError && !uploadTextMutation.isLoading;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {isEditMode
            ? t('dataTextUpload.editTitle')
            : t('dataTextUpload.title')}
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleUpload}>
        <TextField
          fullWidth
          label={t('dataTextUpload.text')}
          variant="outlined"
          multiline
          rows={6}
          value={text}
          onChange={handleTextChange}
          placeholder={t('dataTextUpload.placeholder')}
          disabled={uploadTextMutation.isLoading}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label={t('dataTextUpload.description')}
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('dataTextUpload.descriptionPlaceholder')}
          disabled={uploadTextMutation.isLoading}
          sx={{ mb: 2 }}
        />

        {/* 🔥 GESTION D'ERREURS AVEC REACT QUERY */}
        {textError && (
          <FormHelperText error sx={{ mb: 2 }}>
            {textError}
          </FormHelperText>
        )}

        {uploadTextMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {uploadTextMutation.error?.response?.data?.message ||
              t('dataTextUpload.errorUploading')}
          </Alert>
        )}

        {uploadTextMutation.isSuccess && (
          <Fade in={uploadTextMutation.isSuccess} timeout={1000}>
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('dataTextUpload.uploadSuccess')}
            </Alert>
          </Fade>
        )}

        <Button
          type="submit"
          variant="contained"
          disabled={!isFormValid}
          fullWidth
          startIcon={
            uploadTextMutation.isLoading ? <CircularProgress size={20} /> : null
          }
        >
          {uploadTextMutation.isLoading
            ? isEditMode
              ? t('dataTextUpload.updating')
              : t('dataTextUpload.uploading')
            : isEditMode
              ? t('dataTextUpload.updateText')
              : t('dataTextUpload.uploadText')}
        </Button>
      </Box>
    </Paper>
  );
};

export default DataTextUpload;
